import { BrowserRouter, Routes, Route, useLocation, useNavigationType, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import SmartAppBanner from "./components/SmartAppBanner";
import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import axios from "axios";

// Eager: Homepage + AdDetail + Category (most-trafficked routes — keep in main bundle for fastest LCP)
import HomePage from "./pages/HomePage";
import CategoryPage from "./pages/CategoryPage";
import AdDetailPage from "./pages/AdDetailPage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";

// Lazy: everything else — splits the bundle into per-route chunks (saves ~500KB on initial load)
const CreateAdPage = lazy(() => import("./pages/CreateAdPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const PaymentCallback = lazy(() => import("./pages/PaymentCallback"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const StatusPage = lazy(() => import("./pages/StatusPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const FavoritesPage = lazy(() => import("./pages/FavoritesPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const SellerProfilePage = lazy(() => import("./pages/SellerProfilePage"));
const SellersHubPage = lazy(() => import("./pages/SellersHubPage"));
const PriceAlertsPage = lazy(() => import("./pages/PriceAlertsPage"));
const SavedSearchesPage = lazy(() => import("./pages/SavedSearchesPage"));
const VerificationPage = lazy(() => import("./pages/VerificationPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const OffersPage = lazy(() => import("./pages/OffersPage"));
const SellerDashboardPage = lazy(() => import("./pages/SellerDashboardPage"));
const LoyaltyPage = lazy(() => import("./pages/LoyaltyPage"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const PromotePage = lazy(() => import("./pages/PromotePage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const StoriesPage = lazy(() => import("./pages/StoriesPage"));
const ForumPage = lazy(() => import("./pages/ForumPage"));
const EscrowPage = lazy(() => import("./pages/EscrowPage"));
const AuctionsListPage = lazy(() => import("./pages/AuctionsListPage"));
const AuctionDetailPage = lazy(() => import("./pages/AuctionDetailPage"));
const ApiKeysPage = lazy(() => import("./pages/ApiKeysPage"));
const ApiDocsPage = lazy(() => import("./pages/ApiDocsPage"));
const WebhooksPage = lazy(() => import("./pages/WebhooksPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const MapPage = lazy(() => import("./pages/MapPage"));
const SEOLandingPage = lazy(() => import("./pages/SEOLandingPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentFailurePage = lazy(() => import("./pages/PaymentFailurePage"));

// PWA & Notifications
import { NotificationProvider } from "./contexts/NotificationContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./i18n/LanguageContext";
import PWAPrompt from "./components/PWAPrompt";
import CookieConsent from "./components/CookieConsent";
import SEOVerificationMeta from "./components/SEOVerificationMeta";
import FloatingAddButton from "./components/FloatingAddButton";
import UnifiedNav from "./components/UnifiedNav";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios to always send auth token from localStorage (for cross-domain auth)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if ((config.url || '').includes('/api/auth/me')) {
    // Anonymous visitor: skip the /auth/me network call entirely so the
    // console isn't spammed with 401 errors. Callers hit their normal
    // catch branch exactly as if the server had returned 401.
    const err = new Error('Not authenticated');
    err.response = { status: 401, data: { detail: 'Not authenticated' } };
    err.config = config;
    return Promise.reject(err);
  }
  return config;
});

// Scroll to top on route change (except back/forward navigation)
function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  
  useEffect(() => {
    if (navigationType !== 'POP') {
      // Multiple methods for PWA/Android compatibility
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      // Fallback with requestAnimationFrame for PWA
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    }
  }, [pathname, navigationType]);
  
  return null;
}

// Service Worker Update Listener
function ServiceWorkerUpdater() {
  useEffect(() => {
    // Listen for SW update messages
    if ('serviceWorker' in navigator) {
      // Track the current SW version to detect upgrades (avoid initial-install reload loop)
      const STORAGE_KEY = 'x67_sw_version';
      let pendingReload = false;

      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_UPDATED') {
          const newVersion = event.data.version;
          const knownVersion = localStorage.getItem(STORAGE_KEY);
          if (knownVersion && knownVersion !== newVersion && !pendingReload) {
            // Genuine upgrade — force reload so cached PWA bundles refresh
            pendingReload = true;
            localStorage.setItem(STORAGE_KEY, newVersion);
            // Small delay so the user doesn't see a flash, then hard reload
            setTimeout(() => {
              window.location.reload();
            }, 300);
          } else if (!knownVersion) {
            // First install — just remember the version, no reload needed
            localStorage.setItem(STORAGE_KEY, newVersion);
          }
        }
      });
      
      // Check for updates periodically (every 5 minutes)
      const checkForUpdates = () => {
        navigator.serviceWorker.getRegistration().then((registration) => {
          if (registration) {
            registration.update();
          }
        });
      };
      
      // Check immediately and then every 5 minutes
      checkForUpdates();
      const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, []);
  
  return null;
}

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      // Save token to localStorage if returned (for cross-domain sync)
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }
      setUser(response.data);
    } catch (error) {
      // If auth fails, clear token
      localStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    // Save token to localStorage for cross-domain auth
    if (userData.token) {
      localStorage.setItem('auth_token', userData.token);
    }
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear token from localStorage
    localStorage.removeItem('auth_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - MUST be synchronous during render
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteLoader />}>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/category/:categoryId" element={<CategoryPage />} />
      <Route path="/ad/:adId" element={<AdDetailPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/failure" element={<PaymentFailurePage />} />
      <Route
        path="/create-ad"
        element={
          <ProtectedRoute>
            <CreateAdPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />
      <Route path="/termeni-si-conditii" element={<TermsPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/politica-confidentialitate" element={<PrivacyPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/intrebari-frecvente" element={<FAQPage />} />
      <Route path="/sistem" element={<StatusPage />} />
      <Route path="/map" element={<MapPage />} />
      <Route path="/anunturi/:categorySlug" element={<SEOLandingPage />} />
      <Route path="/anunturi/:categorySlug/:citySlug" element={<SEOLandingPage />} />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <FavoritesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:conversationId"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="/seller/:sellerId" element={<SellerProfilePage />} />
      <Route path="/sellers" element={<SellersHubPage />} />
      <Route
        path="/price-alerts"
        element={
          <ProtectedRoute>
            <PriceAlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved-searches"
        element={
          <ProtectedRoute>
            <SavedSearchesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved-searches/:searchId"
        element={
          <ProtectedRoute>
            <SavedSearchesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/verification"
        element={
          <ProtectedRoute>
            <VerificationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare"
        element={<ComparePage />}
      />
      <Route
        path="/offers"
        element={
          <ProtectedRoute>
            <OffersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller-dashboard"
        element={
          <ProtectedRoute>
            <SellerDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/loyalty"
        element={
          <ProtectedRoute>
            <LoyaltyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral"
        element={
          <ProtectedRoute>
            <ReferralPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/promote"
        element={
          <ProtectedRoute>
            <PromotePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/escrow"
        element={
          <ProtectedRoute>
            <EscrowPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:postId" element={<BlogPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/planuri" element={<PricingPage />} />
      <Route path="/stories" element={<StoriesPage />} />
      <Route path="/forum" element={<ForumPage />} />
      <Route path="/forum/:threadId" element={<ForumPage />} />
      <Route path="/auctions" element={<AuctionsListPage />} />
      <Route path="/auction/:auctionId" element={<AuctionDetailPage />} />
      <Route path="/developer/keys" element={<ApiKeysPage />} />
      <Route path="/developer/docs" element={<ApiDocsPage />} />
      <Route path="/developer/webhooks" element={<WebhooksPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

// Lightweight loader shown while a route chunk is fetched
function RouteLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <NotificationProvider>
              <ScrollToTop />
              <ServiceWorkerUpdater />
              <SEOVerificationMeta />
              <SmartAppBanner />
              <div className="min-h-screen bg-[#050505] overflow-x-hidden pb-24">
                <AppRouter />
                <Toaster position="top-right" richColors />
                <PWAPrompt />
                <CookieConsent />
                <UnifiedNav />
              </div>
            </NotificationProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
